'use client';

import { ReactNode, useEffect } from 'react';
import { IntlError, IntlErrorCode, NextIntlClientProvider } from 'next-intl';
import { useLanguage } from '@/providers/LanguageContext';
import { messagesByLocale } from '@/lib/i18n/messages';
import { defaultLocale } from '@/lib/i18n/locales';
import { log } from '@/lib/log';

/** Route intl errors through the logging wrapper so they reach the error
 * reporter: missing messages are expected while the ternary backlog migrates
 * (breadcrumb-level), anything else (bad ICU syntax, wrong args) is a bug. */
function onIntlError(error: IntlError) {
  if (error.code === IntlErrorCode.MISSING_MESSAGE) {
    log.warn('i18n missing message', error.message);
  } else {
    log.error('i18n error', error);
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const { language } = useLanguage();
  const messages = messagesByLocale[language] ?? messagesByLocale[defaultLocale];

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <NextIntlClientProvider
      locale={language}
      messages={messages}
      // Client-owned locale state has no server request config to inherit.
      // An explicit zone keeps static rendering deterministic and avoids
      // next-intl's ENVIRONMENT_FALLBACK warning; tenant-aware date helpers
      // continue to own institution-specific timezone formatting.
      timeZone="UTC"
      onError={onIntlError}
      getMessageFallback={({ namespace, key }) =>
        namespace ? `${namespace}.${key}` : key
      }
    >
      {children}
    </NextIntlClientProvider>
  );
}
